import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { API, useAuth } from "../App";
import QMSLayout from "../components/QMSLayout";
import axios from "axios";
import {
  FileText, Loader2, ArrowLeft, CheckCircle2, Clock, XCircle,
  AlertTriangle, Send, Eye, Plus, Search, RefreshCw, Edit3,
  ChevronRight, Shield, Truck, ClipboardList, X, Save, Filter
} from "lucide-react";

const ax = axios.create({ withCredentials: true });

const STAGE_CONFIG = {
  draft: { label: "Draft", color: "text-slate-400", bg: "bg-slate-400/10" },
  issued: { label: "Issued", color: "text-[#36A9E1]", bg: "bg-[#36A9E1]/10" },
  supplier_response: { label: "Supplier Response", color: "text-purple-400", bg: "bg-purple-400/10" },
  under_review: { label: "Under Review", color: "text-amber-400", bg: "bg-amber-400/10" },
  verification: { label: "Verification", color: "text-orange-400", bg: "bg-orange-400/10" },
  closed: { label: "Closed", color: "text-emerald-400", bg: "bg-emerald-400/10" },
  cancelled: { label: "Cancelled", color: "text-red-400", bg: "bg-red-400/10" },
};

const PRIORITY_CONFIG = {
  critical: { label: "Critical", color: "text-red-500", bg: "bg-red-500/10" },
  major: { label: "Major", color: "text-amber-400", bg: "bg-amber-400/10" },
  minor: { label: "Minor", color: "text-[#36A9E1]", bg: "bg-[#36A9E1]/10" },
};

const SOURCE_OPTIONS = [
  { value: "audit_finding", label: "Audit Finding" },
  { value: "ncr", label: "NCR" },
  { value: "capa", label: "CAPA" },
  { value: "customer_complaint", label: "Customer Complaint" },
  { value: "incoming_inspection", label: "Incoming Inspection" },
  { value: "process_deviation", label: "Process Deviation" },
  { value: "other", label: "Other" },
];

const SCARManagementPage = () => {
  const { t } = useTranslation('scar');
  const { user } = useAuth();
  const [view, setView] = useState("list"); // list | create | detail | respond | review | verify
  const [scars, setScars] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedScar, setSelectedScar] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/scar/?limit=100`;
      if (filterStatus) url += `&status=${filterStatus}`;
      if (filterPriority) url += `&priority=${filterPriority}`;
      if (searchTerm) url += `&supplier=${encodeURIComponent(searchTerm)}`;

      const [scarsRes, statsRes] = await Promise.all([
        ax.get(url),
        ax.get(`${API}/scar/stats`),
      ]);
      setScars(scarsRes.data.scars || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error("Failed to load SCARs:", err);
    }
    setLoading(false);
  }, [filterStatus, filterPriority, searchTerm]);

  useEffect(() => { loadData(); }, [loadData]);

  const openDetail = async (scarId) => {
    try {
      const res = await ax.get(`${API}/scar/${scarId}`);
      setSelectedScar(res.data);
      setView("detail");
    } catch {}
  };

  const goBack = () => {
    setView("list");
    setSelectedScar(null);
    loadData();
  };

  if (loading && view === "list") {
    return (
      <QMSLayout title={t('scar:management.title')}>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-[#36A9E1] animate-spin" />
        </div>
      </QMSLayout>
    );
  }

  /* ═══ CREATE VIEW ═══ */
  if (view === "create") {
    return (
      <QMSLayout title={t('scar:management.createScar')}>
        <CreateSCARForm onCancel={goBack} onCreated={(scar) => { openDetail(scar.scar_id); }} />
      </QMSLayout>
    );
  }

  /* ═══ DETAIL VIEW ═══ */
  if (view === "detail" && selectedScar) {
    return (
      <QMSLayout title={`SCAR ${selectedScar.scar_id}`}>
        <SCARDetail scar={selectedScar} user={user} onBack={goBack} onRefresh={() => openDetail(selectedScar.scar_id)} />
      </QMSLayout>
    );
  }

  /* ═══ LIST VIEW ═══ */
  return (
    <QMSLayout title={t('scar:management.title')}>
      <div className="space-y-4" data-testid="scar-management">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatCard label={t('management.totalScars')} value={stats.total} icon={ClipboardList} color="text-[#36A9E1]" />
            <StatCard label={t('management.open')} value={stats.open} icon={Clock} color="text-amber-400" />
            <StatCard label={t('management.overdue')} value={stats.overdue} icon={AlertTriangle} color="text-red-400" />
            <StatCard label={t('management.critical')} value={stats.by_priority?.critical || 0} icon={AlertTriangle} color="text-red-500" />
            <StatCard label={t('management.closed')} value={stats.by_status?.closed || 0} icon={CheckCircle2} color="text-emerald-400" />
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8B95A5]" />
              <input type="text" placeholder={t('management.searchSupplier')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded text-sm text-white placeholder-[#8B95A5] w-48"
                data-testid="search-scar" />
            </div>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="px-3 py-2 bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded text-sm text-white" data-testid="filter-scar-status">
              <option value="">{t('management.allStatus')}</option>
              {Object.entries(STAGE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}
              className="px-3 py-2 bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded text-sm text-white" data-testid="filter-scar-priority">
              <option value="">{t('management.allPriority')}</option>
              {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <button onClick={() => setView("create")}
            className="flex items-center gap-2 px-4 py-2 bg-[#36A9E1] hover:bg-[#36A9E1]/80 rounded text-sm text-white font-medium"
            data-testid="create-scar-btn">
            <Plus className="w-4 h-4" /> {t('management.newScar')}
          </button>
        </div>

        {/* SCAR List */}
        {scars.length === 0 ? (
          <div className="text-center py-16 text-[#8B95A5]">
            <Truck className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="text-sm">{t('management.noScarsFound')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {scars.map(scar => {
              const st = STAGE_CONFIG[scar.status] || STAGE_CONFIG.draft;
              const pr = PRIORITY_CONFIG[scar.priority] || PRIORITY_CONFIG.major;
              return (
                <div key={scar.scar_id} className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-4 hover:border-[#36A9E1]/20 transition-all cursor-pointer"
                  onClick={() => openDetail(scar.scar_id)} data-testid={`scar-row-${scar.scar_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-xs text-[#8B95A5] font-mono">{scar.scar_id}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${st.bg} ${st.color}`}>{st.label}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${pr.bg} ${pr.color}`}>{pr.label}</span>
                        </div>
                        <p className="text-sm text-white truncate">{scar.supplier_name}</p>
                        <p className="text-xs text-[#8B95A5] truncate">{scar.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      {scar.due_date && (
                        <span className={`text-[10px] ${scar.due_date < new Date().toISOString().slice(0, 10) && !["closed", "cancelled"].includes(scar.status) ? "text-red-400" : "text-[#8B95A5]"}`}>
                          Due: {scar.due_date}
                        </span>
                      )}
                      <ChevronRight className="w-4 h-4 text-[#8B95A5]" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </QMSLayout>
  );
};

/* ═══════════════════════════════════════════════════════════════ */
/* CREATE SCAR FORM                                                 */
/* ═══════════════════════════════════════════════════════════════ */
const CreateSCARForm = ({ onCancel, onCreated }) => {
  const { t } = useTranslation('scar');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    supplier_name: "", supplier_contact: "", supplier_email: "",
    priority: "major", source: "other", source_reference: "",
    description: "", objective_evidence: "", standard_reference: "",
    assigned_to: "", due_date: "",
  });

  const handleCreate = async () => {
    if (!form.supplier_name.trim()) { alert("Supplier name is required"); return; }
    if (!form.description.trim()) { alert("Description is required"); return; }
    setSaving(true);
    try {
      const res = await ax.post(`${API}/scar/`, form);
      onCreated(res.data);
    } catch (err) {
      alert("Failed: " + (err.response?.data?.detail || err.message));
    }
    setSaving(false);
  };

  const inputCls = "w-full px-3 py-2 bg-[#181B23] border border-[rgba(255,255,255,0.08)] rounded text-sm text-white placeholder-[#8B95A5] focus:outline-none focus:border-[#36A9E1]/50";

  return (
    <div className="space-y-4" data-testid="create-scar-form">
      <button onClick={onCancel} className="flex items-center gap-2 text-sm text-[#8B95A5] hover:text-white" data-testid="create-scar-back">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-6 space-y-4">
        <h3 className="text-white font-medium">{t('create.supplierInfo')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-[#8B95A5] mb-1 block">Supplier Name *</label>
            <input type="text" value={form.supplier_name} onChange={e => setForm({ ...form, supplier_name: e.target.value })}
              className={inputCls} placeholder="Acme Corp" data-testid="scar-supplier-name" />
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] mb-1 block">{t('create.contact')}</label>
            <input type="text" value={form.supplier_contact} onChange={e => setForm({ ...form, supplier_contact: e.target.value })}
              className={inputCls} placeholder="John Smith" />
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] mb-1 block">{t('create.email')}</label>
            <input type="email" value={form.supplier_email} onChange={e => setForm({ ...form, supplier_email: e.target.value })}
              className={inputCls} placeholder="john@acme.com" />
          </div>
        </div>

        <h3 className="text-white font-medium pt-2">{t('create.scarDetails')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-[#8B95A5] mb-1 block">{t('create.priority')}</label>
            <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} className={inputCls} data-testid="scar-priority">
              {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] mb-1 block">{t('create.source')}</label>
            <select value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} className={inputCls} data-testid="scar-source">
              {SOURCE_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] mb-1 block">{t('create.dueDate')}</label>
            <input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} className={inputCls} data-testid="scar-due-date" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[#8B95A5] mb-1 block">Source Reference (NCR/CAPA ID)</label>
            <input type="text" value={form.source_reference} onChange={e => setForm({ ...form, source_reference: e.target.value })}
              className={inputCls} placeholder="NCR-2026-001" />
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] mb-1 block">Standard Reference</label>
            <input type="text" value={form.standard_reference} onChange={e => setForm({ ...form, standard_reference: e.target.value })}
              className={inputCls} placeholder="ISO 13485 Clause 7.4" />
          </div>
        </div>
        <div>
          <label className="text-xs text-[#8B95A5] mb-1 block">Description of Nonconformance *</label>
          <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
            className={`${inputCls} resize-y`} rows={3} placeholder="Describe the issue..." data-testid="scar-description" />
        </div>
        <div>
          <label className="text-xs text-[#8B95A5] mb-1 block">Objective Evidence</label>
          <textarea value={form.objective_evidence} onChange={e => setForm({ ...form, objective_evidence: e.target.value })}
            className={`${inputCls} resize-y`} rows={2} placeholder="Evidence of nonconformance..." />
        </div>

        <div className="flex gap-2 pt-2">
          <button onClick={handleCreate} disabled={saving}
            className="flex items-center gap-2 px-5 py-2 bg-[#36A9E1] hover:bg-[#36A9E1]/80 rounded text-sm text-white font-medium disabled:opacity-50"
            data-testid="submit-scar-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Create SCAR
          </button>
          <button onClick={onCancel} className="px-4 py-2 bg-[#2D313D] text-[#8B95A5] rounded text-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════ */
/* SCAR DETAIL                                                      */
/* ═══════════════════════════════════════════════════════════════ */
const SCARDetail = ({ scar, user, onBack, onRefresh }) => {
  const { t } = useTranslation('scar');
  const [actionLoading, setActionLoading] = useState(false);
  const [showResponse, setShowResponse] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [showVerify, setShowVerify] = useState(false);

  // Response form
  const [responseForm, setResponseForm] = useState({ root_cause: "", corrective_action: "", preventive_action: "", target_date: "" });
  // Review form
  const [reviewForm, setReviewForm] = useState({ accepted: false, review_notes: "" });
  // Verify form
  const [verifyForm, setVerifyForm] = useState({ effective: false, method: "", notes: "" });

  const doAction = async (action, body = {}) => {
    setActionLoading(true);
    try {
      await ax.post(`${API}/scar/${scar.scar_id}/${action}`, body);
      onRefresh();
      setShowResponse(false);
      setShowReview(false);
      setShowVerify(false);
    } catch (err) {
      alert("Action failed: " + (err.response?.data?.detail || err.message));
    }
    setActionLoading(false);
  };

  const st = STAGE_CONFIG[scar.status] || STAGE_CONFIG.draft;
  const pr = PRIORITY_CONFIG[scar.priority] || PRIORITY_CONFIG.major;

  const inputCls = "w-full px-3 py-2 bg-[#181B23] border border-[rgba(255,255,255,0.08)] rounded text-sm text-white placeholder-[#8B95A5] focus:outline-none focus:border-[#36A9E1]/50";

  return (
    <div className="space-y-4" data-testid="scar-detail">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-[#8B95A5] hover:text-white" data-testid="scar-detail-back">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      {/* Header */}
      <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-[#8B95A5] font-mono">{scar.scar_id}</span>
              <span className={`text-xs px-2 py-0.5 rounded ${st.bg} ${st.color}`}>{st.label}</span>
              <span className={`text-xs px-2 py-0.5 rounded ${pr.bg} ${pr.color}`}>{pr.label}</span>
            </div>
            <h3 className="text-lg text-white font-medium">{scar.supplier_name}</h3>
            {scar.supplier_contact && <p className="text-xs text-[#8B95A5]">{scar.supplier_contact} | {scar.supplier_email}</p>}
          </div>
          {scar.due_date && (
            <div className={`text-xs ${scar.due_date < new Date().toISOString().slice(0, 10) && !["closed", "cancelled"].includes(scar.status) ? "text-red-400" : "text-[#8B95A5]"}`}>
              Due: {scar.due_date}
            </div>
          )}
        </div>

        {/* Stage progress */}
        <div className="flex items-center gap-1 my-4 overflow-x-auto">
          {["draft", "issued", "supplier_response", "under_review", "verification", "closed"].map((stage, i) => {
            const allStages = ["draft", "issued", "supplier_response", "under_review", "verification", "closed"];
            const ci = allStages.indexOf(scar.status);
            const si = i;
            const done = si <= ci;
            const active = si === ci;
            return (
              <div key={stage} className="flex items-center gap-1">
                {i > 0 && <div className={`w-4 h-0.5 ${done ? "bg-emerald-400" : "bg-[#2D313D]"}`} />}
                <div className={`px-2 py-1 rounded text-[10px] whitespace-nowrap border ${
                  active ? "border-[#36A9E1] bg-[#36A9E1]/10 text-[#36A9E1]" :
                  done ? "border-emerald-400/30 bg-emerald-400/5 text-emerald-400" :
                  "border-[rgba(255,255,255,0.06)] text-[#8B95A5]"
                }`}>
                  {STAGE_CONFIG[stage]?.label || stage}
                </div>
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 mb-4">
          {scar.status === "draft" && (
            <button onClick={() => doAction("issue", { notes: "SCAR issued to supplier" })} disabled={actionLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#36A9E1]/10 hover:bg-[#36A9E1]/20 text-[#36A9E1] rounded text-xs font-medium disabled:opacity-50"
              data-testid="issue-scar-btn">
              <Send className="w-3.5 h-3.5" /> {t('detail.issueToSupplier')}
            </button>
          )}
          {scar.status === "issued" && (
            <button onClick={() => setShowResponse(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-400/10 hover:bg-purple-400/20 text-purple-400 rounded text-xs font-medium"
              data-testid="respond-scar-btn">
              <Edit3 className="w-3.5 h-3.5" /> {t('detail.recordResponse')}
            </button>
          )}
          {scar.status === "supplier_response" && (
            <button onClick={() => setShowReview(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-400/10 hover:bg-amber-400/20 text-amber-400 rounded text-xs font-medium"
              data-testid="review-scar-btn">
              <Eye className="w-3.5 h-3.5" /> {t('detail.reviewResponse')}
            </button>
          )}
          {scar.status === "verification" && (
            <button onClick={() => setShowVerify(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-400/10 hover:bg-emerald-400/20 text-emerald-400 rounded text-xs font-medium"
              data-testid="verify-scar-btn">
              <CheckCircle2 className="w-3.5 h-3.5" /> {t('detail.verifyEffectiveness')}
            </button>
          )}
          {!["closed", "cancelled"].includes(scar.status) && (
            <button onClick={() => {
              const r = prompt("Cancellation reason:");
              if (r) doAction("cancel", { reason: r });
            }} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-400/10 hover:bg-red-400/20 text-red-400 rounded text-xs" data-testid="cancel-scar-btn">
              <XCircle className="w-3.5 h-3.5" /> Cancel
            </button>
          )}
        </div>

        {/* Details */}
        <div className="space-y-3 text-sm">
          <DetailRow label="Source" value={SOURCE_OPTIONS.find(s => s.value === scar.source)?.label || scar.source} />
          {scar.source_reference && <DetailRow label="Source Ref" value={scar.source_reference} />}
          {scar.standard_reference && <DetailRow label="Standard" value={scar.standard_reference} />}
          <DetailRow label="Description" value={scar.description} />
          {scar.objective_evidence && <DetailRow label="Evidence" value={scar.objective_evidence} />}
        </div>

        {/* Supplier Response */}
        {scar.supplier_response && (
          <div className="mt-4 bg-purple-400/5 border border-purple-400/20 rounded p-3">
            <h4 className="text-xs font-semibold text-purple-400 mb-2">{t('detail.supplierResponse')}</h4>
            <div className="space-y-1 text-xs">
              <DetailRow label={t('management.rootCause')} value={scar.supplier_response.root_cause} small />
              <DetailRow label={t('management.correctiveAction')} value={scar.supplier_response.corrective_action} small />
              {scar.supplier_response.preventive_action && <DetailRow label={t('management.preventiveAction')} value={scar.supplier_response.preventive_action} small />}
            </div>
          </div>
        )}

        {/* Effectiveness */}
        {scar.effectiveness_check && (
          <div className={`mt-4 rounded p-3 border ${scar.effectiveness_check.result === "effective" ? "bg-emerald-400/5 border-emerald-400/20" : "bg-red-400/5 border-red-400/20"}`}>
            <h4 className={`text-xs font-semibold mb-1 ${scar.effectiveness_check.result === "effective" ? "text-emerald-400" : "text-red-400"}`}>
              Effectiveness: {scar.effectiveness_check.result}
            </h4>
            <p className="text-xs text-[#8B95A5]">{scar.effectiveness_check.notes}</p>
          </div>
        )}
      </div>

      {/* Response Form */}
      {showResponse && (
        <div className="bg-[#1F222D] border border-purple-400/20 rounded-lg p-5 space-y-3" data-testid="response-form">
          <h4 className="text-sm font-semibold text-purple-400">{t('detail.supplierResponse')}</h4>
          <div>
            <label className="text-xs text-[#8B95A5] mb-1 block">Root Cause *</label>
            <textarea value={responseForm.root_cause} onChange={e => setResponseForm({ ...responseForm, root_cause: e.target.value })}
              className={`${inputCls} resize-y`} rows={2} />
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] mb-1 block">Corrective Action *</label>
            <textarea value={responseForm.corrective_action} onChange={e => setResponseForm({ ...responseForm, corrective_action: e.target.value })}
              className={`${inputCls} resize-y`} rows={2} />
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] mb-1 block">Preventive Action</label>
            <textarea value={responseForm.preventive_action} onChange={e => setResponseForm({ ...responseForm, preventive_action: e.target.value })}
              className={`${inputCls} resize-y`} rows={2} />
          </div>
          <div className="flex gap-2">
            <button onClick={() => doAction("supplier-response", responseForm)} disabled={actionLoading}
              className="px-4 py-1.5 bg-purple-500 hover:bg-purple-600 text-white rounded text-xs disabled:opacity-50" data-testid="submit-response-btn">
              Submit Response
            </button>
            <button onClick={() => setShowResponse(false)} className="px-4 py-1.5 bg-[#2D313D] text-[#8B95A5] rounded text-xs">Cancel</button>
          </div>
        </div>
      )}

      {/* Review Form */}
      {showReview && (
        <div className="bg-[#1F222D] border border-amber-400/20 rounded-lg p-5 space-y-3" data-testid="review-form">
          <h4 className="text-sm font-semibold text-amber-400">Review Supplier Response</h4>
          <textarea placeholder={t('management.reviewNotes')} value={reviewForm.review_notes} onChange={e => setReviewForm({ ...reviewForm, review_notes: e.target.value })}
            className={`${inputCls} resize-y`} rows={3} />
          <div className="flex gap-2">
            <button onClick={() => doAction("review", { accepted: true, review_notes: reviewForm.review_notes })} disabled={actionLoading}
              className="px-4 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded text-xs disabled:opacity-50" data-testid="accept-response-btn">
              {t('detail.accept')}
            </button>
            <button onClick={() => doAction("review", { accepted: false, review_notes: reviewForm.review_notes })} disabled={actionLoading}
              className="px-4 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded text-xs disabled:opacity-50" data-testid="reject-response-btn">
              {t('detail.reject')}
            </button>
            <button onClick={() => setShowReview(false)} className="px-4 py-1.5 bg-[#2D313D] text-[#8B95A5] rounded text-xs">{t('detail.cancel')}</button>
          </div>
        </div>
      )}

      {/* Verify Form */}
      {showVerify && (
        <div className="bg-[#1F222D] border border-emerald-400/20 rounded-lg p-5 space-y-3" data-testid="verify-form">
          <h4 className="text-sm font-semibold text-emerald-400">Verify Effectiveness</h4>
          <input type="text" placeholder="Verification method..." value={verifyForm.method} onChange={e => setVerifyForm({ ...verifyForm, method: e.target.value })} className={inputCls} />
          <textarea placeholder={t('management.verificationNotes')} value={verifyForm.notes} onChange={e => setVerifyForm({ ...verifyForm, notes: e.target.value })}
            className={`${inputCls} resize-y`} rows={2} />
          <div className="flex gap-2">
            <button onClick={() => doAction("verify-effectiveness", { effective: true, method: verifyForm.method, notes: verifyForm.notes })} disabled={actionLoading}
              className="px-4 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded text-xs disabled:opacity-50" data-testid="verify-effective-btn">
              {t('detail.effective')}
            </button>
            <button onClick={() => doAction("verify-effectiveness", { effective: false, method: verifyForm.method, notes: verifyForm.notes })} disabled={actionLoading}
              className="px-4 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded text-xs disabled:opacity-50" data-testid="verify-not-effective-btn">
              {t('detail.notEffective')}
            </button>
            <button onClick={() => setShowVerify(false)} className="px-4 py-1.5 bg-[#2D313D] text-[#8B95A5] rounded text-xs">{t('detail.cancel')}</button>
          </div>
        </div>
      )}

      {/* Workflow Timeline */}
      <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-5">
        <h4 className="text-xs font-semibold text-[#8B95A5] uppercase tracking-wider mb-3">{t('detail.workflowTimeline')}</h4>
        <div className="space-y-3">
          {(scar.workflow_history || []).map((w, i) => (
            <div key={i} className="flex items-start gap-3 text-xs">
              <div className="w-1.5 h-1.5 rounded-full bg-[#36A9E1] mt-1.5 flex-shrink-0" />
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium">{w.action.replace(/_/g, " ")}</span>
                  <span className={`text-[10px] px-1 py-0 rounded ${(STAGE_CONFIG[w.status] || {}).bg || ""} ${(STAGE_CONFIG[w.status] || {}).color || "text-[#8B95A5]"}`}>
                    {(STAGE_CONFIG[w.status] || {}).label || w.status}
                  </span>
                </div>
                <span className="text-[#8B95A5]">by {w.by_name || w.by} ({w.role})</span>
                {w.notes && <p className="text-[#8B95A5] mt-0.5">{w.notes}</p>}
                <span className="text-[10px] text-[#8B95A5]">{new Date(w.timestamp).toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const DetailRow = ({ label, value, small }) => (
  <div className={`flex ${small ? "gap-2" : "gap-3"}`}>
    <span className={`text-[#8B95A5] ${small ? "text-[10px] w-24" : "text-xs w-28"} flex-shrink-0`}>{label}</span>
    <span className={`text-white ${small ? "text-[10px]" : "text-xs"}`}>{value}</span>
  </div>
);

const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-4 flex items-center gap-3">
    <Icon className={`w-5 h-5 ${color}`} />
    <div>
      <div className="text-xl font-bold text-white">{value}</div>
      <div className="text-xs text-[#8B95A5]">{label}</div>
    </div>
  </div>
);

export default SCARManagementPage;
